/**
 * react-query wiring for `/api/content/*` (brief §content). Query keys are
 * namespaced `["content", …]` per `docs/CONVENTIONS.md`.
 */
import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@client/api/client";
import { formulasQueryKey } from "@client/api/formulas";
import type {
  HomeworkDetailResponse,
  HomeworkListResponse,
  OldExamDetailResponse,
  OldExamsListResponse,
  OverviewResponse,
  PracticePaperDetailResponse,
  PracticePapersListResponse,
} from "./types";

// The formulas query moved to `@client/api/formulas` (shared with the practice
// session's formula panel); same key, so the two features share one cache entry.
export { useFormulas } from "@client/api/formulas";

const STALE_TIME = 5 * 60_000;

export const contentKeys = {
  all: ["content"] as const,
  overview: () => ["content", "overview"] as const,
  formulas: formulasQueryKey,
  oldExamsList: () => ["content", "old-exams"] as const,
  oldExam: (exam: string) => ["content", "old-exams", exam] as const,
  practicePapersList: () => ["content", "practice-papers"] as const,
  practicePaper: (paper: string) => ["content", "practice-papers", paper] as const,
  homeworkList: () => ["content", "homework"] as const,
  homework: (term: string) => ["content", "homework", term] as const,
};

/** Chapters × topics with per-topic counts, for the topic browser. */
export function useContentOverview() {
  return useQuery({
    queryKey: contentKeys.overview(),
    queryFn: () => api.get<OverviewResponse>("/api/content/overview"),
    staleTime: STALE_TIME,
  });
}

/** The archive index: which papers exist, their sit date and problem count. */
export function useOldExamsList() {
  return useQuery({
    queryKey: contentKeys.oldExamsList(),
    queryFn: () => api.get<OldExamsListResponse>("/api/content/old-exams"),
    staleTime: STALE_TIME,
  });
}

/**
 * The full paper (solutions included) for each listed exam term, fetched in
 * parallel — the archive page shows every problem of every paper at once.
 */
export function useOldExamDetails(exams: string[]): UseQueryResult<OldExamDetailResponse>[] {
  return useQueries({
    queries: exams.map((exam) => ({
      queryKey: contentKeys.oldExam(exam),
      queryFn: () =>
        api.get<OldExamDetailResponse>(`/api/content/old-exams/${encodeURIComponent(exam)}`),
      staleTime: STALE_TIME,
    })),
  });
}

/**
 * The practice-paper index. Verified papers only — while the import is still
 * in draft this is an empty list, and the archive section says so rather than
 * pretending the papers do not exist.
 */
export function usePracticePapersList() {
  return useQuery({
    queryKey: contentKeys.practicePapersList(),
    queryFn: () => api.get<PracticePapersListResponse>("/api/content/practice-papers"),
    staleTime: STALE_TIME,
  });
}

/** Every listed practice paper in full, fetched in parallel (as with the past exams). */
export function usePracticePaperDetails(
  papers: string[],
): UseQueryResult<PracticePaperDetailResponse>[] {
  return useQueries({
    queries: papers.map((paper) => ({
      queryKey: contentKeys.practicePaper(paper),
      queryFn: () =>
        api.get<PracticePaperDetailResponse>(
          `/api/content/practice-papers/${encodeURIComponent(paper)}`,
        ),
      staleTime: STALE_TIME,
    })),
  });
}

/** Which terms have hand-ins, and how many. */
export function useHomeworkList() {
  return useQuery({
    queryKey: contentKeys.homeworkList(),
    queryFn: () => api.get<HomeworkListResponse>("/api/content/homework"),
    staleTime: STALE_TIME,
  });
}

/** Each listed term's hand-ins in full, fetched in parallel. */
export function useHomeworkDetails(terms: string[]): UseQueryResult<HomeworkDetailResponse>[] {
  return useQueries({
    queries: terms.map((term) => ({
      queryKey: contentKeys.homework(term),
      queryFn: () =>
        api.get<HomeworkDetailResponse>(`/api/content/homework/${encodeURIComponent(term)}`),
      staleTime: STALE_TIME,
    })),
  });
}
